import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import InactivityGate from "@/components/InactivityGate";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import ClientDetailLayout from "@/components/ClientDetailLayout";
import Login from "@/pages/Login";
import BrandedLogin from "@/pages/BrandedLogin";
import AuthCallback from "@/pages/AuthCallback";
import Overview from "@/pages/Overview";
import Clients from "@/pages/Clients";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import Uploads from "@/pages/Uploads";
import Charts from "@/pages/Charts";
import Reports from "@/pages/Reports";
import Connections from "@/pages/Connections";
import ClientSettingsPage from "@/pages/ClientSettingsPage";
import ClientGoogleFeedsPage from "@/pages/ClientGoogleFeedsPage";
import ClientGoogleFeedPreviewPage from "@/pages/ClientGoogleFeedPreviewPage";
import ClientOpenAIAdsPage from "@/pages/ClientOpenAIAdsPage";
import ClientLinkedInPage from "@/pages/ClientLinkedInPage";
import ClientMetaAdsPage from "@/pages/ClientMetaAdsPage";
import ClientChecklistPage from "@/pages/ClientChecklistPage";
import ClientMicrosoftPage from "@/pages/ClientMicrosoftPage";
import ClientPinterestPage from "@/pages/ClientPinterestPage";
import ClientTikTokPage from "@/pages/ClientTikTokPage";
import ClientDashboardPage from "@/pages/ClientDashboardPage";
import ClientChannelPage from "@/pages/ClientChannelPage";
import ClientProductReviewPage from "@/pages/ClientProductReviewPage";
import ClientProductReviewDetailPage from "@/pages/ClientProductReviewDetailPage";
import ClientTrustpilotPage from "@/pages/ClientTrustpilotPage";
import ClientSendGridPage from "@/pages/ClientSendGridPage";
import ClientBingFeedsPage from "@/pages/ClientBingFeedsPage";
import ClientAhrefsFeedsPage from "@/pages/ClientAhrefsFeedsPage";
import AhrefsKeywordOverlapPage from "@/pages/AhrefsKeywordOverlapPage";
import AhrefsMasterPage from "@/pages/AhrefsMasterPage";
import PsiMasterPage from "@/pages/PsiMasterPage";
import ClientPsiPage from "@/pages/ClientPsiPage";
import WoorankMasterPage from "@/pages/WoorankMasterPage";
import ClientWoorankPage from "@/pages/ClientWoorankPage";
import DomainsPage from "@/pages/DomainsPage";
import ClientDomainsPage from "@/pages/ClientDomainsPage";
import ClientDomainDetailPage from "@/pages/ClientDomainDetailPage";
import ClientHostingPage from "@/pages/ClientHostingPage";
import ClientLegalPage from "@/pages/ClientLegalPage";
import PublicLegalSignPage from "@/pages/PublicLegalSignPage";
import IpApiPage from "@/pages/IpApiPage";
import MozApiPage from "@/pages/MozApiPage";
import WordfenceMasterPage from "@/pages/WordfenceMasterPage";
import ClientWordfencePage from "@/pages/ClientWordfencePage";
import ClientStrategyPlannerPage from "@/pages/ClientStrategyPlannerPage";
import ClientStrategyV2Page from "@/pages/ClientStrategyV2Page";
import StrategyPlaybookManagerPage from "@/pages/StrategyPlaybookManagerPage";
import StrategyShareView from "@/pages/StrategyShareView";
import ClientPhonesPage from "@/pages/ClientPhonesPage";
import ClientLocationsPage from "@/pages/ClientLocationsPage";
import ClientLocationDetailPage from "@/pages/ClientLocationDetailPage";
import ClientGoogleReviewsPage from "@/pages/ClientGoogleReviewsPage";
import ClientCalendarPage from "@/pages/ClientCalendarPage";
import MasterCalendarPage from "@/pages/MasterCalendarPage";
import PortalCalendarRedirect from "@/pages/PortalCalendarPage";
import SeedanceIntegrationsPage from "@/pages/SeedanceIntegrationsPage";
import Settings from "@/pages/Settings";
import GoogleIntegrationsPage from "@/pages/GoogleIntegrationsPage";
import MicrosoftIntegrationsPage from "@/pages/MicrosoftIntegrationsPage";
import MetaIntegrationsPage from "@/pages/MetaIntegrationsPage";
import MetaAppReviewPage from "@/pages/MetaAppReviewPage";
import LinkedInIntegrationsPage from "@/pages/LinkedInIntegrationsPage";
import LinkedInOAuthCallbackPage from "@/pages/LinkedInOAuthCallbackPage";
import ClientMozDataPage from "@/pages/ClientMozDataPage";
import ClientBriefPage from "@/pages/ClientBriefPage";
import ClientProposalsPage from "@/pages/ClientProposalsPage";
import ProposalEditorPage from "@/pages/ProposalEditorPage";
import ProposalShareView from "@/pages/ProposalShareView";
import TemplatesListPage from "@/pages/TemplatesListPage";
import PropertiesPage from "@/pages/PropertiesPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import ContactsPage from "@/pages/ContactsPage";
import HistoricalStatsPage from "@/pages/HistoricalStatsPage";
import MasterAIPrompts from "@/pages/MasterAIPrompts";
import MasterTeamMembers from "@/pages/MasterTeamMembers";
import AdminTeamPage from "@/pages/AdminTeamPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import SchedulerPage from "@/pages/SchedulerPage";
import SendGridMasterPage from "@/pages/SendGridMasterPage";
import TwilioMasterPage from "@/pages/TwilioMasterPage";
import ThreeCxMasterPage from "@/pages/ThreeCxMasterPage";
import VoiceAgentsPage from "@/pages/VoiceAgentsPage";
import AiPhoneAgentsPage from "@/pages/AiPhoneAgentsPage";
import AiPhoneAgentEditorPage from "@/pages/AiPhoneAgentEditorPage";
import ThreeCxCallCentrePage from "@/pages/ThreeCxCallCentrePage";
import AIUsagePage from "@/pages/AIUsagePage";
import AIStudioPage from "@/pages/AIStudioPage";
import CreativePage from "@/pages/CreativePage";
import CreativeLibraryPage from "@/pages/CreativeLibraryPage";
import AutoPostQueuePage from "@/pages/AutoPostQueuePage";
import SiteMetaPage from "@/pages/SiteMetaPage";
import ViewReportPage from "@/pages/ViewReportPage";
import MetaOAuthCallback from "@/pages/MetaOAuthCallback";
import XeroMasterPage from "@/pages/XeroMasterPage";
import XeroOAuthCallback from "@/pages/XeroOAuthCallback";
import KeyPayPage from "@/pages/KeyPayPage";
import XeroBankRecPage from "@/pages/XeroBankRecPage";
import XeroInvoicesPage from "@/pages/XeroInvoicesPage";
import XeroInvoiceDetailPage from "@/pages/XeroInvoiceDetailPage";
import XeroContactsPage from "@/pages/XeroContactsPage";
import XeroReportsPage from "@/pages/XeroReportsPage";
import XeroRollupPage from "@/pages/XeroRollupPage";
import VaultSettingsPage from "@/pages/VaultSettingsPage";
import AIAccessPage from "@/pages/AIAccessPage";
import ImpersonationLogPage from "@/pages/ImpersonationLogPage";
import VaultedRoute from "@/components/VaultedRoute";
import KeyPayClientDashboard from "@/pages/KeyPayClientDashboard";
import KeyPayEmployeeDetail from "@/pages/KeyPayEmployeeDetail";
import KeyPayRollupPage from "@/pages/KeyPayRollupPage";
import KeyPayPayrunDetailPage from "@/pages/KeyPayPayrunDetailPage";
import KeyPayLeaveCalendarPage from "@/pages/KeyPayLeaveCalendarPage";
import KeyPayTimesheetsPage from "@/pages/KeyPayTimesheetsPage";
import KeyPayPayChangesPage from "@/pages/KeyPayPayChangesPage";
import KeyPaySuperPaymentsPage from "@/pages/KeyPaySuperPaymentsPage";
import FairWorkPage from "@/pages/FairWorkPage";
import AtoAccessPage from "@/pages/AtoAccessPage";
import KeyPayCompliancePage from "@/pages/KeyPayCompliancePage";
import InvoicingPage from "@/pages/InvoicingPage";
import RecurringInvoicesPage from "@/pages/RecurringInvoicesPage";
import InvoiceEditorPage from "@/pages/InvoiceEditorPage";
import InvoiceDetailPage from "@/pages/InvoiceDetailPage";
import PublicInvoicePayPage from "@/pages/PublicInvoicePayPage";
import BillsPage from "@/pages/BillsPage";
import LegalDocsPage from "@/pages/LegalDocsPage";
import RecurringBillsPage from "@/pages/RecurringBillsPage";
import BillDetailPage from "@/pages/BillDetailPage";
import MerchantSettingsPage from "@/pages/MerchantSettingsPage";
import PaymentsPage from "@/pages/PaymentsPage";
import PaymentsPayPalPage from "@/pages/PaymentsPayPalPage";
import EntitlementHoldingsPage from "@/pages/EntitlementHoldingsPage";
import TaxHoldingsPage from "@/pages/TaxHoldingsPage";
import CashflowForecastPage from "@/pages/CashflowForecastPage";
import BankRecReportsPage from "@/pages/BankRecReportsPage";
import DomainCostsPage from "@/pages/DomainCostsPage";
import WordPressPage from "@/pages/WordPressPage";
import WpSiteDetailPage from "@/pages/WpSiteDetailPage";
import WpBeaconDashboardPage from "@/pages/WpBeaconDashboardPage";
import HostServersPage from "@/pages/HostServersPage";
import HostAccountDetailPage from "@/pages/HostAccountDetailPage";
import WorkLogsPage from "@/pages/WorkLogsPage";
import ProposalsPipelinePage from "@/pages/ProposalsPipelinePage";
import SalesPipelinePage from "@/pages/SalesPipelinePage";
import LeadEditorPage from "@/pages/LeadEditorPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import TaskManagerPage from "@/pages/TaskManagerPage";
import TicketsPage from "@/pages/TicketsPage";
import TicketDetailPage from "@/pages/TicketDetailPage";
import PortalTicketsPage from "@/pages/PortalTicketsPage";
import BreakdancePage from "@/pages/BreakdancePage";
import WooCommercePage from "@/pages/WooCommercePage";
import SemrushMasterPage from "@/pages/SemrushMasterPage";
import ApiUsagePage from "@/pages/ApiUsagePage";
import SecurityStatsPage from "@/pages/SecurityStatsPage";
import { VaultProvider } from "@/contexts/VaultContext";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/dashboard" : "/portal/dashboard"} replace />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=") && location.pathname !== "/auth/callback") {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/c/:code/login" element={<BrandedLogin />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/proposals/share/:token" element={<ProposalShareView />} />
      <Route path="/pay/:token" element={<PublicInvoicePayPage />} />
      <Route path="/strategy/share/:token" element={<StrategyShareView />} />
      <Route path="/proposals/preview/:proposalId" element={<ProposalShareView mode="preview" />} />
      <Route path="/sign-agreement/:token" element={<PublicLegalSignPage />} />

      {/* Standalone AHREFS keyword-overlap full-window view — auth
          protected, but rendered WITHOUT the AppShell chrome so admins
          get maximum horizontal + vertical real estate for the overlap
          table. Reachable via the "Open in full window" button on any
          KeywordOverlapPanel. */}
      <Route path="/clients/:clientId/ahrefs-feeds/:assignmentId/keyword-overlap"
        element={<ProtectedRoute adminOnly><AhrefsKeywordOverlapPage /></ProtectedRoute>} />

      {/* Admin */}
      <Route element={<ProtectedRoute adminOnly><AppShell /></ProtectedRoute>}>
        <Route path="/clients" element={<Clients />} />
        <Route path="/dashboard" element={<AdminDashboardPage />} />
        <Route path="/domains" element={<DomainsPage />} />
        <Route path="/wordpress" element={<WordPressPage />} />
        <Route path="/wordpress/sites/:siteId" element={<WpSiteDetailPage />} />
        <Route path="/wp-beacon" element={<WpBeaconDashboardPage />} />
        <Route path="/host-servers" element={<HostServersPage />} />
        <Route path="/host-servers/:serverId/accounts/:whmUser" element={<HostAccountDetailPage />} />
        <Route path="/work-logs" element={<WorkLogsPage />} />
        <Route path="/proposals" element={<ProposalsPipelinePage />} />
        <Route path="/sales-pipeline" element={<SalesPipelinePage />} />
        <Route path="/sales-pipeline/new" element={<LeadEditorPage />} />
        <Route path="/sales-pipeline/leads/:leadId" element={<LeadEditorPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="/tasks" element={<TaskManagerPage />} />
        <Route path="/tickets" element={<TicketsPage />} />
        <Route path="/tickets/:ticketId" element={<TicketDetailPage />} />
        <Route path="/breakdance" element={<BreakdancePage />} />
        <Route path="/woocommerce" element={<WooCommercePage />} />
        <Route path="/calendar" element={<MasterCalendarPage />} />
        <Route path="/auto-post-queue" element={<AutoPostQueuePage />} />
        <Route path="/ai-prompts" element={<MasterAIPrompts />} />
        <Route path="/team-members" element={<MasterTeamMembers />} />
        <Route path="/templates" element={<TemplatesListPage />} />
        <Route path="/templates/:templateId" element={<ProposalEditorPage mode="template" />} />
        <Route path="/admin-team" element={<AdminTeamPage />} />
        <Route path="/admin/strategy-playbook" element={<StrategyPlaybookManagerPage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/integrations/google" element={<GoogleIntegrationsPage />} />
        <Route path="/integrations/microsoft" element={<MicrosoftIntegrationsPage />} />
        <Route path="/integrations/meta" element={<MetaIntegrationsPage />} />
        <Route path="/integrations/meta/app-review" element={<MetaAppReviewPage />} />
        <Route path="/integrations/meta/callback" element={<MetaOAuthCallback />} />
        <Route path="/integrations/linkedin" element={<LinkedInIntegrationsPage />} />
        <Route path="/integrations/linkedin/callback" element={<LinkedInOAuthCallbackPage />} />
        <Route path="/integrations/xero" element={<VaultedRoute><XeroMasterPage /></VaultedRoute>} />
        <Route path="/invoicing" element={<VaultedRoute><InvoicingPage /></VaultedRoute>} />
        <Route path="/legal-docs" element={<VaultedRoute><LegalDocsPage /></VaultedRoute>} />
        <Route path="/invoicing/recurring" element={<VaultedRoute><RecurringInvoicesPage /></VaultedRoute>} />
        <Route path="/invoicing/invoices/new" element={<VaultedRoute><InvoiceEditorPage /></VaultedRoute>} />
        <Route path="/invoicing/invoices/:invoiceId" element={<VaultedRoute><InvoiceDetailPage /></VaultedRoute>} />
        <Route path="/invoicing/invoices/:invoiceId/edit" element={<VaultedRoute><InvoiceEditorPage /></VaultedRoute>} />
        <Route path="/invoicing/recurring/new" element={<VaultedRoute><InvoiceEditorPage recurring /></VaultedRoute>} />
        <Route path="/invoicing/recurring/:recurringId/edit" element={<VaultedRoute><InvoiceEditorPage recurring /></VaultedRoute>} />
        <Route path="/bills" element={<VaultedRoute><BillsPage /></VaultedRoute>} />
        <Route path="/bills/recurring" element={<VaultedRoute><RecurringBillsPage /></VaultedRoute>} />
        <Route path="/bills/:billId" element={<VaultedRoute><BillDetailPage /></VaultedRoute>} />
        <Route path="/merchant-settings" element={<VaultedRoute><MerchantSettingsPage /></VaultedRoute>} />
        <Route path="/payments" element={<VaultedRoute><PaymentsPage /></VaultedRoute>} />
        <Route path="/payments/paypal" element={<VaultedRoute><PaymentsPayPalPage /></VaultedRoute>} />
        <Route path="/entitlement-holdings" element={<VaultedRoute><EntitlementHoldingsPage /></VaultedRoute>} />
        <Route path="/tax-holdings" element={<VaultedRoute><TaxHoldingsPage /></VaultedRoute>} />
        <Route path="/cashflow-forecast" element={<VaultedRoute><CashflowForecastPage /></VaultedRoute>} />
        <Route path="/bank-rec" element={<VaultedRoute><BankRecReportsPage /></VaultedRoute>} />
        <Route path="/domain-costs" element={<VaultedRoute><DomainCostsPage /></VaultedRoute>} />
        <Route path="/integrations/xero/callback" element={<VaultedRoute><XeroOAuthCallback /></VaultedRoute>} />
        <Route path="/integrations/xero/tenants/:tenantId/bank-rec" element={<VaultedRoute><XeroBankRecPage /></VaultedRoute>} />
        <Route path="/integrations/xero/tenants/:tenantId/invoices" element={<VaultedRoute><XeroInvoicesPage /></VaultedRoute>} />
        <Route path="/integrations/xero/tenants/:tenantId/invoices/:invoiceId" element={<VaultedRoute><XeroInvoiceDetailPage /></VaultedRoute>} />
        <Route path="/integrations/xero/tenants/:tenantId/contacts" element={<VaultedRoute><XeroContactsPage /></VaultedRoute>} />
        <Route path="/integrations/xero/tenants/:tenantId/reports" element={<VaultedRoute><XeroReportsPage /></VaultedRoute>} />
        <Route path="/integrations/xero/rollup" element={<VaultedRoute><XeroRollupPage /></VaultedRoute>} />
        <Route path="/integrations/keypay" element={<VaultedRoute><KeyPayPage /></VaultedRoute>} />
        <Route path="/integrations/keypay/rollup" element={<VaultedRoute><KeyPayRollupPage /></VaultedRoute>} />
        <Route path="/integrations/keypay/clients/:clientId" element={<VaultedRoute><KeyPayClientDashboard /></VaultedRoute>} />
        <Route path="/integrations/keypay/clients/:clientId/employees/:employeeId" element={<VaultedRoute><KeyPayEmployeeDetail /></VaultedRoute>} />
        <Route path="/integrations/keypay/clients/:clientId/payruns/:payrunId" element={<VaultedRoute><KeyPayPayrunDetailPage /></VaultedRoute>} />
        <Route path="/integrations/keypay/clients/:clientId/leave-calendar" element={<VaultedRoute><KeyPayLeaveCalendarPage /></VaultedRoute>} />
        <Route path="/integrations/keypay/clients/:clientId/timesheets" element={<VaultedRoute><KeyPayTimesheetsPage /></VaultedRoute>} />
        <Route path="/integrations/keypay/clients/:clientId/pay-changes" element={<VaultedRoute><KeyPayPayChangesPage /></VaultedRoute>} />
        <Route path="/integrations/keypay/clients/:clientId/super-payments" element={<VaultedRoute><KeyPaySuperPaymentsPage /></VaultedRoute>} />
        <Route path="/fairwork" element={<VaultedRoute><FairWorkPage /></VaultedRoute>} />
        <Route path="/integrations/ato" element={<VaultedRoute><AtoAccessPage /></VaultedRoute>} />
        <Route path="/integrations/keypay/clients/:clientId/compliance" element={<VaultedRoute><KeyPayCompliancePage /></VaultedRoute>} />
        <Route path="/vault-settings" element={<VaultSettingsPage />} />
        <Route path="/ai-access" element={<AIAccessPage />} />
        <Route path="/admin/impersonation-log" element={<ImpersonationLogPage />} />
        <Route path="/scheduler" element={<SchedulerPage />} />
        <Route path="/security-stats" element={<SecurityStatsPage />} />
        <Route path="/ip-api" element={<IpApiPage />} />
        <Route path="/moz-api" element={<MozApiPage />} />
        <Route path="/integrations/sendgrid" element={<SendGridMasterPage />} />
        <Route path="/integrations/ahrefs" element={<AhrefsMasterPage />} />
        <Route path="/integrations/semrush" element={<SemrushMasterPage />} />
        <Route path="/integrations/psi" element={<PsiMasterPage />} />
        <Route path="/integrations/woorank" element={<WoorankMasterPage />} />
        <Route path="/integrations/wordfence" element={<WordfenceMasterPage />} />
        <Route path="/integrations/seedance" element={<SeedanceIntegrationsPage />} />
        <Route path="/integrations/twilio" element={<TwilioMasterPage />} />
        <Route path="/integrations/3cx" element={<ThreeCxMasterPage />} />
        <Route path="/integrations/3cx/call-centre" element={<ThreeCxCallCentrePage />} />
        <Route path="/integrations/voice-agents" element={<VoiceAgentsPage />} />
        <Route path="/ai-phone-agents" element={<AiPhoneAgentsPage />} />
        <Route path="/ai-phone-agents/:agentId" element={<AiPhoneAgentEditorPage />} />
        <Route path="/ai-usage" element={<AIUsagePage />} />
        <Route path="/service-usage" element={<ApiUsagePage />} />
        <Route path="/site-meta" element={<SiteMetaPage />} />

        {/* Client detail with nested tabs */}
        <Route path="/clients/:clientId" element={<ClientDetailLayout />}>
          <Route index element={<Overview />} />
          <Route path="proposals" element={<ClientProposalsPage />} />
          <Route path="proposals/:proposalId" element={<ProposalEditorPage />} />
          <Route path="calendar" element={<ClientCalendarPage />} />
          <Route path="calendar/:activityId/ai-studio" element={<AIStudioPage />} />
          <Route path="creative" element={<CreativePage />} />
          <Route path="library" element={<CreativeLibraryPage />} />
          <Route path="brief" element={<ClientBriefPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="domains" element={<ClientDomainsPage />} />
          <Route path="domains/:domainId" element={<ClientDomainDetailPage />} />
          <Route path="hosting" element={<ClientHostingPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/:buId" element={<PropertyDetailPage />} />
          <Route path="uploads" element={<Uploads />} />
          <Route path="historical" element={<HistoricalStatsPage />} />
          <Route path="worklogs" element={<Uploads worklogsOnly />} />
          <Route path="legal" element={<ClientLegalPage />} />
          <Route path="charts" element={<Charts />} />
          <Route path="reports" element={<Reports />} />
          <Route path="reports/:reportId/view" element={<ViewReportPage />} />
          <Route path="connections" element={<Connections />} />
          <Route path="google-feeds" element={<ClientGoogleFeedsPage />} />
          <Route path="bing-feeds" element={<ClientBingFeedsPage />} />
          <Route path="ahrefs-feeds" element={<ClientAhrefsFeedsPage />} />
          <Route path="psi" element={<ClientPsiPage />} />
          <Route path="woorank" element={<ClientWoorankPage />} />
          <Route path="moz-data" element={<ClientMozDataPage />} />
          <Route path="wordfence" element={<ClientWordfencePage />} />
          <Route path="strategy" element={<ClientStrategyPlannerPage />} />
          <Route path="strategy-v2" element={<ClientStrategyV2Page />} />
          <Route path="google-feeds/:service/:resourceId" element={<ClientGoogleFeedPreviewPage />} />
          <Route path="openai-ads" element={<ClientOpenAIAdsPage />} />
          <Route path="linkedin" element={<ClientLinkedInPage />} />
          <Route path="meta-ads" element={<ClientMetaAdsPage />} />
          <Route path="microsoft" element={<ClientMicrosoftPage />} />
          <Route path="pinterest" element={<ClientPinterestPage />} />
          <Route path="tiktok" element={<ClientTikTokPage />} />
          <Route path="product-review" element={<ClientProductReviewPage />} />
          <Route path="product-review/:eventId" element={<ClientProductReviewDetailPage />} />
          <Route path="trustpilot" element={<ClientTrustpilotPage />} />
          <Route path="sendgrid" element={<ClientSendGridPage />} />
          <Route path="phones" element={<ClientPhonesPage />} />
          <Route path="locations" element={<ClientLocationsPage />} />
          <Route path="locations/:locationId" element={<ClientLocationDetailPage />} />
          <Route path="google-reviews" element={<ClientGoogleReviewsPage />} />
          <Route path="checklist" element={<ClientChecklistPage />} />
          <Route path="settings" element={<ClientSettingsPage />} />
        </Route>
      </Route>

      {/* Client portal */}
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/portal" element={<Overview />} />
        <Route path="/portal/dashboard" element={<ClientDashboardPage />} />
        <Route path="/portal/channels/:key" element={<ClientChannelPage />} />
        <Route path="/portal/charts" element={<Charts />} />
        <Route path="/portal/reports" element={<Reports />} />
        <Route path="/portal/reports/:reportId/view" element={<ViewReportPage />} />
        <Route path="/portal/domains" element={<ClientDomainsPage />} />
        <Route path="/portal/domains/:domainId" element={<ClientDomainDetailPage />} />
        <Route path="/portal/tickets" element={<PortalTicketsPage />} />
        <Route path="/portal/calendar" element={<PortalCalendarRedirect />} />
        <Route path="/portal/calendar/:clientId" element={<ClientCalendarPage />} />
        <Route path="/portal/calendar/:clientId/:activityId/ai-studio" element={<AIStudioPage />} />
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <VaultProvider>
          <AppRoutes />
          <InactivityGate />
          <Toaster position="top-right" richColors />
        </VaultProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
